import { useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { formatCurrency, formatNumber } from "../lib/format.js";
import { budgetGeometry, humanizeUnitValue, nearestIndex, slotIndex } from "../lib/chart-math.js";

// --- shared hover / tooltip mechanism -------------------------------------

interface HoverState {
  fx: number;
  fy: number;
}

interface HoverHandlers {
  onPointerMove?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerLeave?: () => void;
}

function useChartHover(enabled: boolean): { hover: HoverState | null; handlers: HoverHandlers } {
  const [hover, setHover] = useState<HoverState | null>(null);
  if (!enabled) return { hover: null, handlers: {} };
  const track = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    setHover({
      fx: Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1),
      fy: Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1),
    });
  };
  return {
    hover,
    handlers: { onPointerMove: track, onPointerDown: track, onPointerLeave: () => setHover(null) },
  };
}

const tooltipStyle: CSSProperties = {
  position: "absolute",
  zIndex: 6,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  boxShadow: "var(--shadow-md)",
  padding: "6px 9px",
  fontSize: 11.5,
  lineHeight: 1.45,
  color: "var(--fg)",
  pointerEvents: "none",
  whiteSpace: "nowrap",
};

function ChartTooltip({ fx, fy, children }: { fx: number; fy: number; children: ReactNode }) {
  // Flip to the left of the cursor past the midpoint so the tooltip never
  // overflows the card horizontally.
  return (
    <div
      style={{
        ...tooltipStyle,
        left: `${fx * 100}%`,
        top: `${Math.min(fy, 0.72) * 100}%`,
        transform: fx > 0.55 ? "translate(calc(-100% - 12px), 0)" : "translate(12px, 0)",
      }}
    >
      {children}
    </div>
  );
}

const axisLabelStyle: CSSProperties = {
  position: "absolute",
  right: 2,
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  color: "var(--fg-faint)",
  pointerEvents: "none",
  whiteSpace: "nowrap",
};

// --- Sparkline -------------------------------------------------------------

interface SparklineProps {
  points: number[];
  color?: string;
  width?: number;
  height?: number;
  fluid?: boolean;
  threshold?: number;
  thresholdColor?: string;
}

function scaleY(v: number, max: number, h: number): number {
  return h - 2 - (v / max) * (h - 6);
}

function toPolyline(points: number[], max: number, w: number, h: number): string {
  const n = points.length;
  return points
    .map((v, i) => `${((n > 1 ? i / (n - 1) : 0) * (w - 2) + 1).toFixed(1)},${scaleY(v, max, h).toFixed(1)}`)
    .join(" ");
}

export function Sparkline({
  points,
  color = "var(--accent-dot)",
  width = 116,
  height = 28,
  fluid,
  threshold,
  thresholdColor = "var(--tone-alarm-dot)",
}: SparklineProps) {
  if (points.length === 0) return null;
  const max = Math.max(...points, threshold ?? 0) * 1.12 || 1;
  const pts = toPolyline(points, max, width, height);
  return (
    <svg
      width={fluid ? "100%" : width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={fluid ? "none" : "xMidYMid meet"}
      style={{ display: "block", flex: "none" }}
      aria-hidden="true"
    >
      {threshold !== undefined && (
        <line
          x1={1}
          x2={width - 1}
          y1={scaleY(threshold, max, height)}
          y2={scaleY(threshold, max, height)}
          stroke={thresholdColor}
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// --- AreaSparkline -----------------------------------------------------------

interface AreaSparklineProps {
  points: number[];
  color?: string;
  width?: number | string;
  height?: number;
  showGrid?: boolean;
  xLabels?: string[];
  threshold?: number;
  thresholdLabel?: string;
  thresholdColor?: string;
  hover?: boolean;
  unit?: string;
  formatValue?: (v: number) => string;
  xLabelFor?: (index: number) => string;
  showAxis?: boolean;
}

const VW = 100;
const VH = 40;
const GRID_FRACTIONS = [0.28, 0.55, 0.82];

export function AreaSparkline({
  points,
  color = "var(--accent-dot)",
  width = "100%",
  height = 130,
  showGrid,
  xLabels,
  threshold,
  thresholdLabel,
  thresholdColor = "var(--tone-alarm-dot)",
  hover,
  unit,
  formatValue,
  xLabelFor,
  showAxis,
}: AreaSparklineProps) {
  const n = points.length;
  const { hover: hv, handlers } = useChartHover(Boolean(hover) && n > 0);
  if (n === 0) return null;
  const max = (Math.max(...points, threshold ?? 0) * 1.14) || 1;
  const sy = (v: number) => VH - 1.5 - (v / max) * (VH - 5);
  const sx = (i: number) => (n > 1 ? i / (n - 1) : 0) * VW;
  const line = points.map((v, i) => `${i === 0 ? "M" : "L"}${sx(i).toFixed(2)},${sy(v).toFixed(2)}`).join("");
  const area = `M${sx(0).toFixed(2)},${(VH - 1).toFixed(2)}${points
    .map((v, i) => `L${sx(i).toFixed(2)},${sy(v).toFixed(2)}`)
    .join("")}L${sx(n - 1).toFixed(2)},${(VH - 1).toFixed(2)}Z`;
  const fmt = formatValue ?? formatNumber;
  const hi = hv === null ? null : nearestIndex(n, hv.fx);
  const hiXPct = hi === null ? 0 : (sx(hi) / VW) * 100;
  const hiYPct = hi === null ? 0 : (sy(points[hi]) / VH) * 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div {...handlers} style={{ position: "relative", touchAction: hover ? "pan-y" : undefined }}>
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="none"
          width={width}
          height={height}
          style={{ display: "block" }}
          aria-hidden="true"
        >
          {(showGrid || showAxis) &&
            GRID_FRACTIONS.map((f) => (
              <line
                key={f}
                x1={0}
                x2={VW}
                y1={VH * f}
                y2={VH * f}
                stroke="var(--divider)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          <path d={area} fill={color} fillOpacity={0.1} stroke="none" />
          <path
            d={line}
            fill="none"
            stroke={color}
            strokeWidth={1.6}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {threshold !== undefined && (
            <line
              x1={0}
              x2={VW}
              y1={sy(threshold)}
              y2={sy(threshold)}
              stroke={thresholdColor}
              strokeWidth={1.2}
              strokeDasharray="5 4"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {showAxis &&
          GRID_FRACTIONS.map((f) => (
            <div key={f} style={{ ...axisLabelStyle, top: `calc(${f * 100}% - 12px)` }}>
              {fmt(((VH - 1.5 - VH * f) / (VH - 5)) * max)}
              {unit ?? ""}
            </div>
          ))}
        {threshold !== undefined && thresholdLabel && (
          <div
            style={{
              position: "absolute",
              top: Math.max(0, (sy(threshold) / VH) * height - 16),
              right: 0,
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              color: thresholdColor,
              pointerEvents: "none",
            }}
          >
            {thresholdLabel}
          </div>
        )}
        {hv !== null && hi !== null && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${hiXPct}%`,
                width: 1,
                background: "var(--border-strong)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: `${hiXPct}%`,
                top: `${hiYPct}%`,
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: color,
                border: "1.5px solid var(--surface)",
                boxSizing: "border-box",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            />
            <ChartTooltip fx={hv.fx} fy={hv.fy}>
              {xLabelFor && <span style={{ color: "var(--fg-subtle)" }}>{xLabelFor(hi)} · </span>}
              <span className="mono" style={{ fontWeight: 500 }}>
                {fmt(points[hi])}
                {unit ?? ""}
              </span>
            </ChartTooltip>
          </>
        )}
      </div>
      {xLabels && xLabels.length > 0 && <XAxisLabels labels={xLabels} />}
    </div>
  );
}

// --- MetricAxisChart ---------------------------------------------------------

interface MetricAxisChartProps {
  points: number[];
  unit: string;
  color?: string;
  height?: number;
  xLabels?: string[];
  xLabelFor?: (index: number) => string;
  threshold?: number;
  thresholdLabel?: string;
  thresholdColor?: string;
}

// Thin preset over AreaSparkline: hover + y-axis labels, values humanized per
// CloudWatch unit (Percent -> %, Bytes -> KiB/MiB/GiB, Seconds -> ms, else plain).
export function MetricAxisChart({
  points,
  unit,
  color,
  height = 180,
  xLabels,
  xLabelFor,
  threshold,
  thresholdLabel,
  thresholdColor,
}: MetricAxisChartProps) {
  return (
    <AreaSparkline
      points={points}
      color={color}
      height={height}
      xLabels={xLabels}
      xLabelFor={xLabelFor}
      threshold={threshold}
      thresholdLabel={thresholdLabel}
      thresholdColor={thresholdColor}
      hover
      showAxis
      formatValue={(v) => humanizeUnitValue(v, unit)}
    />
  );
}

// --- StackedBars ---------------------------------------------------------------

interface StackedBarsSeries {
  label: string;
  color: string;
  values: number[];
}

interface StackedBarsProps {
  series: StackedBarsSeries[];
  labels: string[];
  anomalyIndex?: number;
  height?: number;
  hover?: boolean;
  formatValue?: (v: number) => string;
  showYAxis?: boolean;
  slotLabelFor?: (index: number) => string;
}

function roundedTopPath(x: number, y: number, w: number, h: number): string {
  const rx = Math.min(w * 0.3, 0.7);
  const ry = Math.min(h * 0.5, 0.6);
  return [
    `M${(x).toFixed(2)},${(y + ry).toFixed(2)}`,
    `Q${x.toFixed(2)},${y.toFixed(2)} ${(x + rx).toFixed(2)},${y.toFixed(2)}`,
    `L${(x + w - rx).toFixed(2)},${y.toFixed(2)}`,
    `Q${(x + w).toFixed(2)},${y.toFixed(2)} ${(x + w).toFixed(2)},${(y + ry).toFixed(2)}`,
    `L${(x + w).toFixed(2)},${(y + h).toFixed(2)}`,
    `L${x.toFixed(2)},${(y + h).toFixed(2)}Z`,
  ].join("");
}

export function StackedBars({
  series,
  labels,
  anomalyIndex,
  height = 150,
  hover,
  formatValue,
  showYAxis,
  slotLabelFor,
}: StackedBarsProps) {
  const n = series.length > 0 ? series[0].values.length : 0;
  const { hover: hv, handlers } = useChartHover(Boolean(hover) && n > 0);
  if (series.length === 0 || n === 0) return null;
  const totals = Array.from({ length: n }, (_, i) =>
    series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0),
  );
  const yMax = Math.max(...totals) * 1.12 || 1;
  const slot = VW / n;
  const bw = slot * 0.6;
  const fmt = formatValue ?? formatNumber;
  const hi = hv === null ? null : slotIndex(n, hv.fx);
  const slots: ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const x = i * slot + (slot - bw) / 2;
    let cum = 0;
    const topIdx = series.reduce((acc, s, si) => ((s.values[i] ?? 0) > 0 ? si : acc), 0);
    const segments: ReactNode[] = [];
    series.forEach((s, si) => {
      const v = s.values[i] ?? 0;
      const hh = Math.max(0.2, (v / yMax) * (VH - 3));
      cum += hh;
      const y = VH - 1 - cum;
      if (si === topIdx) {
        segments.push(<path key={si} d={roundedTopPath(x, y, bw, hh)} fill={s.color} />);
      } else {
        segments.push(<rect key={si} x={x} y={y} width={bw} height={hh} fill={s.color} />);
      }
    });
    slots.push(
      <g key={i} opacity={hi !== null && hi !== i ? 0.4 : 1}>
        {segments}
      </g>,
    );
  }
  const hoverLabel =
    hi === null ? "" : slotLabelFor ? slotLabelFor(hi) : labels.length === n ? labels[hi] : `#${hi + 1}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div {...handlers} style={{ position: "relative", touchAction: hover ? "pan-y" : undefined }}>
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="none"
          width="100%"
          height={height}
          style={{ display: "block" }}
          aria-hidden="true"
        >
          {GRID_FRACTIONS.map((f) => (
            <line
              key={f}
              x1={0}
              x2={VW}
              y1={VH * f}
              y2={VH * f}
              stroke="var(--divider)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {slots}
          {anomalyIndex !== undefined && anomalyIndex >= 0 && (
            <line
              x1={anomalyIndex * slot}
              x2={anomalyIndex * slot}
              y1={1}
              y2={VH - 1}
              stroke="var(--fg-subtle)"
              strokeWidth={1}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {showYAxis &&
          [1, 0.5, 0].map((frac) => {
            const topPct = ((VH - 1 - frac * (VH - 3)) / VH) * 100;
            return (
              <div
                key={frac}
                style={{
                  ...axisLabelStyle,
                  top: frac === 1 ? 0 : `calc(${topPct}% - 11px)`,
                }}
              >
                {fmt(yMax * frac)}
              </div>
            );
          })}
        {anomalyIndex !== undefined && anomalyIndex >= 0 && (
          <div
            style={{
              position: "absolute",
              top: -2,
              // Anchor right of the marker unless it sits near the right edge,
              // where the label would overflow the card.
              ...(anomalyIndex / n > 0.65
                ? { right: `${(1 - anomalyIndex / n) * 100}%`, transform: "translateX(-4px)" }
                : { left: `${(anomalyIndex / n) * 100}%`, transform: "translateX(4px)" }),
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-subtle)",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            Anomaly
          </div>
        )}
        {hv !== null && hi !== null && (
          <ChartTooltip fx={hv.fx} fy={hv.fy}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 130 }}>
              {hoverLabel && (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--fg-subtle)",
                  }}
                >
                  {hoverLabel}
                </div>
              )}
              {series.map((s) => (
                <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{ width: 7, height: 7, borderRadius: 2, background: s.color, flex: "none" }}
                  />
                  <span style={{ color: "var(--fg-subtle)" }}>{s.label}</span>
                  <span className="mono" style={{ marginLeft: "auto", paddingLeft: 14 }}>
                    {fmt(s.values[hi] ?? 0)}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  borderTop: "1px solid var(--divider)",
                  paddingTop: 3,
                }}
              >
                <span style={{ fontWeight: 600 }}>Total</span>
                <span className="mono" style={{ marginLeft: "auto", paddingLeft: 14, fontWeight: 600 }}>
                  {fmt(totals[hi])}
                </span>
              </div>
            </div>
          </ChartTooltip>
        )}
      </div>
      {labels.length > 0 && <XAxisLabels labels={labels} />}
    </div>
  );
}

// --- BudgetBar --------------------------------------------------------------

interface BudgetBarProps {
  actual: number;
  forecast: number | null;
  limit: number;
  currency: string;
  height?: number;
}

const CAPTION_SPACE = 15;

export function BudgetBar({ actual, forecast, limit, currency, height = 12 }: BudgetBarProps) {
  const geo = budgetGeometry(actual, forecast, limit);
  const { hover: hv, handlers } = useChartHover(true);
  const fillColor =
    geo.tone === "alarm"
      ? "var(--tone-alarm-dot)"
      : geo.tone === "warn"
        ? "var(--tone-warn-dot)"
        : "var(--accent)";
  return (
    <div {...handlers} style={{ position: "relative", paddingTop: CAPTION_SPACE, touchAction: "pan-y" }}>
      <div
        style={{
          position: "relative",
          height,
          borderRadius: 8,
          background: "var(--bg-muted)",
          overflow: "hidden",
        }}
      >
        {geo.forecastFrac !== null && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: `${Math.min(geo.forecastFrac, geo.limitFrac) * 100}%`,
              background: fillColor,
              opacity: 0.22,
              borderRadius: 8,
            }}
          />
        )}
        {geo.forecastFrac !== null && geo.forecastFrac > geo.limitFrac && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${geo.limitFrac * 100}%`,
              width: `${(geo.forecastFrac - geo.limitFrac) * 100}%`,
              background: "var(--tone-alarm-dot)",
              opacity: 0.55,
              borderRadius: "0 8px 8px 0",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: `${geo.actualFrac * 100}%`,
            background: fillColor,
            borderRadius: 8,
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          top: CAPTION_SPACE - 5,
          bottom: 0,
          left: `calc(${geo.limitFrac * 100}% - 1px)`,
          width: 2,
          borderRadius: 1,
          background: "var(--fg-subtle)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          ...(geo.limitFrac > 0.85
            ? { right: `${(1 - geo.limitFrac) * 100}%` }
            : { left: `${geo.limitFrac * 100}%`, transform: "translateX(-50%)" }),
          fontSize: 8.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-faint)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        limit
      </div>
      {hv !== null && (
        <ChartTooltip fx={hv.fx} fy={hv.fy}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span>
              <span style={{ color: "var(--fg-subtle)" }}>Actual </span>
              <span className="mono" style={{ fontWeight: 500 }}>{formatCurrency(actual, currency)}</span>
              {forecast !== null && (
                <>
                  <span style={{ color: "var(--fg-subtle)" }}> · Forecast </span>
                  <span className="mono" style={{ fontWeight: 500 }}>
                    {formatCurrency(forecast, currency)}
                  </span>
                </>
              )}
              <span style={{ color: "var(--fg-subtle)" }}> · Limit </span>
              <span className="mono" style={{ fontWeight: 500 }}>{formatCurrency(limit, currency)}</span>
            </span>
            {geo.overAmount > 0 && (
              <span style={{ color: "var(--tone-alarm-fg)" }}>
                Over by{" "}
                <span className="mono" style={{ fontWeight: 500 }}>
                  {formatCurrency(geo.overAmount, currency)}
                </span>{" "}
                <span className="mono">(+{geo.overPct.toFixed(0)}%)</span>
              </span>
            )}
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

// --- shared x-axis labels row ----------------------------------------------

function XAxisLabels({ labels }: { labels: string[] }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        color: "var(--fg-faint)",
      }}
    >
      {labels.map((t, i) => (
        <span key={`${t}-${i}`}>{t}</span>
      ))}
    </div>
  );
}
